<template>
  <div class="exam-management">
    <el-row :gutter="20">
      <!-- Room Management -->
      <el-col :span="6">
        <el-card>
          <template #header>
            <div class="card-header">
              <span>考场管理</span>
              <div style="display: flex; gap: 8px;">
                <el-button size="small" @click="showBatchImportRoomsDialog">
                  <el-icon>
                    <MagicStick />
                  </el-icon>
                  批量导入
                </el-button>
                <el-button type="primary" size="small" @click="showRoomDialog">
                  <el-icon>
                    <Plus />
                  </el-icon>
                </el-button>
              </div>
            </div>
          </template>
          <el-table :data="rooms" v-loading="loadingRooms" size="small">
            <el-table-column prop="name" label="考场" width="80" />
            <el-table-column prop="alias" label="别名" width="100" />
            <el-table-column prop="building" label="楼栋" />
            <el-table-column prop="capacity" label="容量" width="60" />
            <el-table-column label="操作" width="100">
              <template #default="{ row }">
                <el-button text type="primary" size="small" @click="editRoom(row)">编辑</el-button>
                <el-popconfirm title="确定删除？" @confirm="deleteRoom(row.id)">
                  <template #reference>
                    <el-button text type="danger" size="small">删除</el-button>
                  </template>
                </el-popconfirm>
              </template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-col>

      <!-- Exam List -->
      <el-col :span="18">
        <el-card>
          <template #header>
            <div class="card-header">
              <div>
                <span>考试列表</span>
                <el-select v-model="selectedSemesterId" placeholder="选择学期" size="small"
                  style="margin-left: 16px; width: 200px" @change="handleSemesterChange">
                  <el-option v-for="s in semesters" :key="s.id" :label="s.name" :value="s.id" />
                </el-select>
              </div>
              <el-button type="primary" @click="showExamDialog" :disabled="!selectedSemesterId">
                <el-icon>
                  <Plus />
                </el-icon>
                新建考试
              </el-button>
            </div>
          </template>

          <el-table :data="exams" v-loading="loadingExams" stripe>
            <el-table-column prop="name" label="考试名称" min-width="200" />
            <el-table-column label="场次数" width="100">
              <template #default="{ row }">
                {{ row.sessions?.length || 0 }} 场
              </template>
            </el-table-column>
            <el-table-column label="科目/年级" min-width="200">
              <template #default="{ row }">
                <el-tag v-for="s in row.sessions?.filter(sess => sess.subject_name)" :key="s.id" size="small"
                  :type="getGradeTagType(s.grade)" style="margin-right: 4px; margin-bottom: 2px">
                  {{ s.grade }} {{ s.subject_name }}
                </el-tag>
                <span v-if="!row.sessions?.some(s => s.subject_name)" style="color: #6b7280">未配置</span>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="260" fixed="right">
              <template #default="{ row }">
                <el-button text type="primary" @click="configureExam(row)">配置</el-button>
                <el-button text type="primary" @click="editExam(row)">编辑</el-button>
                <el-button type="success" @click="goToSchedule(row.id)" :disabled="!row.sessions?.length">
                  排考
                </el-button>
                <el-popconfirm title="确定删除？" @confirm="deleteExam(row.id)">
                  <template #reference>
                    <el-button text type="danger">删除</el-button>
                  </template>
                </el-popconfirm>
              </template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-col>
    </el-row>

    <!-- Room Dialog -->
    <el-dialog v-model="roomDialogVisible" :title="roomForm.id ? '编辑考场' : '新建考场'" width="400px">
      <el-form :model="roomForm" label-width="80px">
        <el-form-item label="考场名称">
          <el-input v-model="roomForm.name" placeholder="如：101、A201" />
        </el-form-item>
        <el-form-item label="别名">
          <el-input v-model="roomForm.alias" placeholder="如：高三1考场" />
        </el-form-item>
        <el-form-item label="楼栋">
          <el-input v-model="roomForm.building" placeholder="如：教学楼A" />
        </el-form-item>
        <el-form-item label="容量">
          <el-input-number v-model="roomForm.capacity" :min="10" :max="200" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="roomDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitRoom" :loading="submitting">确定</el-button>
      </template>
    </el-dialog>

    <!-- Exam Dialog -->
    <el-dialog v-model="examDialogVisible" :title="examForm.id ? '编辑考试' : '新建考试'" width="500px">
      <el-form :model="examForm" label-width="100px">
        <el-form-item label="考试名称">
          <el-input v-model="examForm.name" placeholder="如：2025-2026学年第一学期期中考试" />
        </el-form-item>
        <el-form-item label="描述">
          <el-input v-model="examForm.description" type="textarea" rows="2" />
        </el-form-item>
        
        <!-- 监考匹配规则 -->
        <el-form-item label="监考规则">
          <el-radio-group v-model="examForm.grade_rule_type" @change="handleGradeRuleTypeChange">
            <el-radio label="isolated">完全隔离</el-radio>
            <el-radio label="cross">交叉监考</el-radio>
            <el-radio label="mixed">完全混合</el-radio>
            <el-radio label="custom">自定义分组</el-radio>
          </el-radio-group>
          <div class="rule-hint" style="font-size: 12px; color: #909399; margin-top: 4px; line-height: 1.4;">
            <span v-if="examForm.grade_rule_type === 'isolated'">💡 <b>完全隔离模式</b>：教师只能监考自己所属年级的考试。</span>
            <span v-else-if="examForm.grade_rule_type === 'cross'">💡 <b>交叉监考模式</b>：同一分组内跨年级互相监考，且任课老师不监考自己所属年级。可直接使用下方快捷预设。</span>
            <span v-else-if="examForm.grade_rule_type === 'mixed'">💡 <b>完全混合模式</b>：全校教师打通，所有老师可被随机分配到任意年级监考。</span>
            <span v-else-if="examForm.grade_rule_type === 'custom'">💡 <b>自定义分组模式</b>：指定某些年级进行混合监考，未加入任何组的年级将自动单独成组。</span>
          </div>
        </el-form-item>

        <el-form-item v-if="['custom', 'cross'].includes(examForm.grade_rule_type)" label="配置分组">
          <div style="width: 100%;">
            <div v-if="examForm.grade_rule_type === 'cross'" style="display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 12px;">
              <el-button
                v-for="preset in crossGroupPresets"
                :key="preset.label"
                size="small"
                type="primary"
                plain
                @click="applyCrossPreset(preset.groups)"
              >
                {{ preset.label }}
              </el-button>
            </div>
            <!-- Group Cards -->
            <div v-for="(group, gIdx) in customGroups" :key="gIdx" class="group-card" style="border: 1px solid #dcdfe6; border-radius: 6px; padding: 12px; margin-bottom: 12px; background-color: #fafafa;">
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
                <span style="font-size: 13px; font-weight: bold; color: #409eff;">🏫 {{ examForm.grade_rule_type === 'cross' ? '交叉组' : '混合组' }} {{ gIdx + 1 }}</span>
                <el-button v-if="customGroups.length > 1" type="text" size="small" style="color: #f56c6c; padding: 0; height: auto;" @click="deleteGroup(gIdx)">删除此组</el-button>
              </div>
              
              <!-- Tags in this Group -->
              <div style="display: flex; flex-wrap: wrap; gap: 8px; align-items: center;">
                <el-tag
                  v-for="(grade, idx) in group"
                  :key="idx"
                  closable
                  :type="getGradeTagType(grade)"
                  @close="removeGradeFromGroup(gIdx, idx)"
                  style="font-size: 12px; font-weight: bold;"
                >
                  {{ grade }}
                </el-tag>
                
                <!-- Add Grade dropdown in this Group -->
                <el-dropdown trigger="click" @command="(cmd) => addGradeToGroup(gIdx, cmd)" v-if="unassignedGrades.length > 0">
                  <el-button size="small" type="primary" plain style="padding: 2px 8px; font-size: 11px;">+ 添加年级</el-button>
                  <template #dropdown>
                    <el-dropdown-menu>
                      <el-dropdown-item v-for="g in unassignedGrades" :key="g" :command="g">
                        {{ g }}
                      </el-dropdown-item>
                    </el-dropdown-menu>
                  </template>
                </el-dropdown>
              </div>
            </div>

            <!-- Action buttons -->
            <div style="display: flex; justify-content: space-between; align-items: center; margin-top: 8px;">
              <el-button size="small" type="primary" plain @click="addNewGroup" :disabled="unassignedGrades.length === 0">
                + 新建{{ examForm.grade_rule_type === 'cross' ? '交叉组' : '混合组' }}
              </el-button>
              <span v-if="unassignedGrades.length > 0" style="font-size: 12px; color: #909399;">
                待分配年级: 
                <el-tag v-for="g in unassignedGrades" :key="g" size="small" type="info" style="margin-left: 4px;">{{ g }}</el-tag>
              </span>
              <span v-else style="font-size: 12px; color: #67c23a;">
                🎉 所有年级已全部分配完毕
              </span>
            </div>
            <div style="font-size: 12px; color: #909399; margin-top: 6px; line-height: 1.4;">
              {{ examForm.grade_rule_type === 'cross' ? '交叉组内会强制跨年级安排，老师不会监考自己任教年级；单独成组的年级无法交叉。' : '未加入任何组的年级将自动单独成组。' }}
            </div>
          </div>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="examDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitExam" :loading="submitting">确定</el-button>
      </template>
    </el-dialog>

    <!-- Exam Configuration Dialog - 新版：场次直接包含科目和考场 -->
    <el-dialog v-model="configDialogVisible" :title="`配置考试：${currentExam?.name || ''}`" width="900px">
      <div class="config-section">
        <h4 style="margin-bottom: 16px">添加考试场次</h4>
        <el-form inline @submit.prevent="addSession">
          <el-form-item label="日期">
            <el-date-picker v-model="sessionForm.exam_date" type="date" value-format="YYYY-MM-DD"
              style="width: 130px" />
          </el-form-item>
          <el-form-item label="开始">
            <el-time-picker v-model="sessionForm.start_time" format="HH:mm" value-format="HH:mm:ss"
              style="width: 100px" />
          </el-form-item>
          <el-form-item label="结束">
            <el-time-picker v-model="sessionForm.end_time" format="HH:mm" value-format="HH:mm:ss"
              style="width: 100px" />
          </el-form-item>
          <el-form-item label="年级">
            <el-select v-model="sessionForm.grade" style="width: 100px">
              <el-option v-for="g in allPossibleGrades" :key="g" :label="g" :value="g" />
            </el-select>
          </el-form-item>
          <el-form-item label="科目">
            <el-select v-model="sessionForm.subject_id" style="width: 100px">
              <el-option v-for="s in subjects" :key="s.id" :label="s.name" :value="s.id" />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="addSession">添加场次</el-button>
            <el-button type="success" @click="showBatchImportDialog">批量导入</el-button>
          </el-form-item>
        </el-form>

        <el-table :data="currentExam?.sessions || []" size="small" style="margin-top: 16px">
          <el-table-column prop="exam_date" label="日期" width="110" />
          <el-table-column label="时间" width="130">
            <template #default="{ row }">
              {{ formatTime(row.start_time) }} - {{ formatTime(row.end_time) }}
            </template>
          </el-table-column>
          <el-table-column label="年级" width="70">
            <template #default="{ row }">
              <el-tag :type="getGradeTagType(row.grade)" size="small">
                {{ row.grade || '未设置' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="科目" width="80">
            <template #default="{ row }">
              {{ row.subject_name || '未设置' }}
            </template>
          </el-table-column>
          <el-table-column label="考场">
            <template #default="{ row }">
              <el-tag v-for="r in row.exam_rooms" :key="r.id" size="small" type="info" style="margin-right: 4px"
                closable @close="removeRoomFromSession(row.id, r.room_id)">
                {{ r.room_name }} ({{ r.required_invigilators }}人)
              </el-tag>
              <el-button text type="primary" size="small" @click="showRoomAssignDialog(row)">
                配置考场
              </el-button>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="80">
            <template #default="{ row }">
              <el-popconfirm title="确定删除？" @confirm="deleteSession(row.id)">
                <template #reference>
                  <el-button text type="danger" size="small">删除</el-button>
                </template>
              </el-popconfirm>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </el-dialog>

    <!-- Room Assignment Dialog -->
    <el-dialog v-model="roomAssignDialogVisible" title="配置考场" width="500px">
      <el-form label-width="120px">
        <el-form-item label="选择考场">
          <div style="width: 100%; margin-bottom: 8px">
            <el-checkbox v-model="selectAllRooms" @change="handleSelectAllRooms">全选</el-checkbox>
          </div>
          <el-checkbox-group v-model="selectedRoomIds" @change="handleRoomSelectionChange">
            <el-checkbox v-for="r in rooms" :key="r.id" :label="r.id">
              {{ r.building }} {{ r.name }}
            </el-checkbox>
          </el-checkbox-group>
        </el-form-item>
        <el-form-item label="每室监考人数">
          <el-input-number v-model="roomAssignForm.required_invigilators" :min="1" :max="5" />
        </el-form-item>
        <el-form-item label="批量应用">
          <el-checkbox v-model="applyToAllSessions">应用到该考试【所有年级】的其他场次</el-checkbox>
        </el-form-item>
        <el-form-item label="按年级应用" v-if="currentSession?.grade">
          <div style="display: flex; flex-direction: column; gap: 4px;">
            <el-checkbox v-model="applyToGradeThisExam">应用到该考试【{{ currentSession.grade }}】的其他场次</el-checkbox>
            <el-checkbox v-model="applyToGradeOtherExams">应用到本学期其他考试的【{{ currentSession.grade }}】场次</el-checkbox>
          </div>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="roomAssignDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitRoomAssignment" :loading="submitting">确定</el-button>
      </template>
    </el-dialog>

    <!-- Batch Import Dialog -->
    <el-dialog v-model="batchImportDialogVisible" title="批量导入场次" width="760px">
      <div class="batch-import-container">
        <el-tabs v-model="batchImportMode">
          <el-tab-pane label="Excel 导入" name="excel">
            <div class="import-hint">
              <p>请先下载 Excel 模板，按模板填写考试日期、开始时间、结束时间、年级、科目。</p>
              <p>日期建议填写为 <code>2026-01-20</code>；时间建议填写为 <code>08:00</code> 或 <code>08:00:00</code>。</p>
              <p>科目按名称填写，系统不存在的科目会在导入时自动创建。导入后再为场次配置考场。</p>
            </div>
            <el-button type="primary" plain @click="downloadSessionTemplate">
              <el-icon><Download /></el-icon>
              下载 Excel 模板
            </el-button>
            <el-upload
              ref="sessionImportUploadRef"
              action=""
              :auto-upload="false"
              :limit="1"
              accept=".xlsx,.xls"
              :on-change="handleSessionExcelChange"
              :on-remove="handleSessionExcelRemove"
              style="margin-top: 14px;"
            >
              <el-button>选择 Excel 文件</el-button>
              <template #tip>
                <div class="el-upload__tip">列名需包含：考试日期、开始时间、结束时间、年级、科目</div>
              </template>
            </el-upload>
          </el-tab-pane>
          <el-tab-pane label="粘贴导入" name="text">
            <div class="import-hint">
              <p>请输入场次信息，每行一个场次。格式：<strong>日期 时间段 年级 科目</strong></p>
              <p>例如：<code>2026-01-20 08:00-10:00 高一 语文</code></p>
              <p><strong>智能识别支持：</strong>支持空格/制表符/逗号等任意分隔符，支持中文冒号（如 08：00）、日期和时间段的中文横线连接（如 2026—01—20、08:00—10:00）、额外秒数（如 08:00:00）或不同空格格式的自动智能矫正。</p>
            </div>
            <el-input v-model="batchImportText" type="textarea" :rows="10" placeholder="粘贴场次信息到这里..."
              @input="parseBatchText" />

            <div v-if="parsedSessions.length > 0" class="preview-section">
              <h4>解析预览 ({{ parsedSessions.length }} 场)</h4>
              <el-table :data="parsedSessions" size="small" max-height="250">
                <el-table-column prop="exam_date" label="日期" width="100" />
                <el-table-column label="时间" width="120">
                  <template #default="{ row }">
                    {{ row.start_time }}-{{ row.end_time }}
                  </template>
                </el-table-column>
                <el-table-column prop="grade" label="年级" width="70" />
                <el-table-column prop="subject_name" label="科目" />
                <el-table-column label="状态" width="80">
                  <template #default="{ row }">
                    <el-tag :type="row.subject_id ? 'success' : 'danger'" size="small">
                      {{ row.subject_id ? '就绪' : '科目未知' }}
                    </el-tag>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </el-tab-pane>
        </el-tabs>
      </div>
      <template #footer>
        <el-button @click="batchImportDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitBatchImport" :loading="submitting"
          :disabled="batchImportMode === 'excel' ? !sessionImportExcelFile : parsedSessions.length === 0 || parsedSessions.some(s => !s.subject_id)">
          确认导入
        </el-button>
      </template>
    </el-dialog>

    <!-- Batch Import Rooms Dialog -->
    <el-dialog v-model="batchImportRoomsDialogVisible" title="批量导入考场" width="700px">
      <div class="batch-import-container">
        <div class="import-hint" style="background-color: #ecf5ff; border-left: 5px solid #409eff; color: #606266; padding: 10px 15px; margin-bottom: 15px; font-size: 13px;">
          <p>请输入考场信息，每行一个考场。格式：<strong>楼栋 考场号 容量 别名(可选)</strong></p>
          <p>支持空格或制表符分隔。例如：</p>
          <p><code>教学楼A 101 30</code></p>
          <p><code>综合楼B B204 45 高三考场</code></p>
        </div>
        <el-input v-model="batchImportRoomsText" type="textarea" :rows="10" placeholder="粘贴考场信息到这里..."
          @input="parseBatchRoomsText" />

        <div v-if="parsedRooms.length > 0" class="preview-section" style="margin-top: 20px;">
          <h4 style="margin-bottom: 10px; color: #303133; font-weight: 500;">解析预览 ({{ parsedRooms.length }} 个考场)</h4>
          <el-table :data="parsedRooms" size="small" border max-height="250">
            <el-table-column prop="building" label="楼栋" width="150" />
            <el-table-column prop="name" label="考场名称" width="120" />
            <el-table-column prop="capacity" label="容量" width="100" />
            <el-table-column prop="alias" label="别名">
              <template #default="{ row }">
                {{ row.alias || '-' }}
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
      <template #footer>
        <el-button @click="batchImportRoomsDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitBatchRoomsImport" :loading="submitting"
          :disabled="parsedRooms.length === 0">
          确认导入
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { Plus, MagicStick, Download } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { semesterApi, teacherApi, examApi } from '@/api'

const router = useRouter()

const semesters = ref([])
const subjects = ref([])
const rooms = ref([])
const exams = ref([])
const selectedSemesterId = ref(null)

const loadingRooms = ref(false)
const loadingExams = ref(false)
const submitting = ref(false)

const roomDialogVisible = ref(false)
const examDialogVisible = ref(false)
const configDialogVisible = ref(false)
const roomAssignDialogVisible = ref(false)
const batchImportDialogVisible = ref(false)
const batchImportRoomsDialogVisible = ref(false)
const batchImportRoomsText = ref('')
const parsedRooms = ref([])
const currentExam = ref(null)
const currentSession = ref(null)

const batchImportText = ref('')
const parsedSessions = ref([])
const batchImportMode = ref('excel')
const sessionImportExcelFile = ref(null)
const sessionImportUploadRef = ref(null)

const roomForm = ref({ id: null, name: '', alias: '', building: '', capacity: 30 })
const examForm = ref({ id: null, name: '', description: '', grade_rule_type: 'isolated', grade_rule_groups: '' })

const allPossibleGrades = ref([])
const customGroups = ref([])

const unassignedGrades = computed(() => {
  const assigned = new Set(customGroups.value.flat())
  return allPossibleGrades.value.filter(g => !assigned.has(g))
})

const normalizeGroupsWithSingles = (groups) => {
  const cleanGroups = groups
    .map(group => group.filter(g => allPossibleGrades.value.includes(g)))
    .filter(group => group.length > 0)
  const assigned = new Set(cleanGroups.flat())
  allPossibleGrades.value.forEach(g => {
    if (!assigned.has(g)) {
      cleanGroups.push([g])
    }
  })
  return cleanGroups.length > 0 ? cleanGroups : allPossibleGrades.value.map(g => [g])
}

const crossGroupPresets = computed(() => {
  const pairPresets = []
  for (let i = 0; i < allPossibleGrades.value.length - 1; i += 1) {
    const pair = [allPossibleGrades.value[i], allPossibleGrades.value[i + 1]]
    pairPresets.push({
      label: `${pair[0]} ↔ ${pair[1]}`,
      groups: [pair]
    })
  }

  const presets = [...pairPresets]
  if (allPossibleGrades.value.length >= 2) {
    presets.push({
      label: '全部年级互相交叉',
      groups: [allPossibleGrades.value]
    })
  }

  return presets
})

const applyCrossPreset = (groups) => {
  customGroups.value = normalizeGroupsWithSingles(groups)
}

const applyDefaultCrossPreset = () => {
  if (allPossibleGrades.value.length >= 2) {
    applyCrossPreset([[allPossibleGrades.value[0], allPossibleGrades.value[1]]])
  } else {
    customGroups.value = allPossibleGrades.value.map(g => [g])
  }
}

const handleGradeRuleTypeChange = (type) => {
  if (type === 'cross') {
    applyDefaultCrossPreset()
  }
}


const addNewGroup = () => {
  customGroups.value.push([])
}

const deleteGroup = (gIdx) => {
  customGroups.value.splice(gIdx, 1)
}

const removeGradeFromGroup = (gIdx, idx) => {
  customGroups.value[gIdx].splice(idx, 1)
}

const addGradeToGroup = (gIdx, grade) => {
  customGroups.value[gIdx].push(grade)
}

const getGroupsPayload = () => {
  const groups = customGroups.value.map(g => [...g]).filter(g => g.length > 0)
  const assigned = new Set(groups.flat())
  allPossibleGrades.value.forEach(g => {
    if (!assigned.has(g)) {
      groups.push([g])
    }
  })
  return JSON.stringify(groups)
}
const sessionForm = ref({
  exam_date: '',
  start_time: '',
  end_time: '',
  session_name: '',
  subject_id: null,
  grade: ''
})
const selectedRoomIds = ref([])
const roomAssignForm = ref({ required_invigilators: 1 })
const selectAllRooms = ref(false)
const applyToAllSessions = ref(false)
const applyToGradeThisExam = ref(false)
const applyToGradeOtherExams = ref(false)

const handleSelectAllRooms = (val) => {
  selectedRoomIds.value = val ? rooms.value.map(r => r.id) : []
}

const handleRoomSelectionChange = (value) => {
  selectAllRooms.value = value.length === rooms.value.length && rooms.value.length > 0
}

const updatePossibleGrades = () => {
  const sem = semesters.value.find(s => s.id === selectedSemesterId.value)
  if (sem && sem.available_grades) {
    allPossibleGrades.value = sem.available_grades.split(',').map(g => g.trim()).filter(g => g.length > 0)
  } else {
    allPossibleGrades.value = []
  }
  
  if (allPossibleGrades.value.length > 0 && !allPossibleGrades.value.includes(sessionForm.value.grade)) {
    sessionForm.value.grade = allPossibleGrades.value[0]
  }
}

const handleSemesterChange = () => {
  updatePossibleGrades()
  fetchExams()
}

const fetchSemesters = async () => {
  try {
    semesters.value = await semesterApi.getAll()
    if (semesters.value.length > 0) {
      const active = semesters.value.find(s => s.is_active)
      selectedSemesterId.value = active?.id || semesters.value[0].id
      updatePossibleGrades()
      fetchExams()
    }
  } catch (e) {
    console.error(e)
  }
}

const fetchSubjects = async () => {
  try {
    subjects.value = await teacherApi.getSubjects()
  } catch (e) {
    console.error(e)
  }
}

const fetchRooms = async () => {
  loadingRooms.value = true
  try {
    rooms.value = await examApi.getRooms()
  } catch (e) {
    console.error(e)
  } finally {
    loadingRooms.value = false
  }
}

const fetchExams = async () => {
  if (!selectedSemesterId.value) return
  loadingExams.value = true
  try {
    exams.value = await examApi.getAll(selectedSemesterId.value)
  } catch (e) {
    console.error(e)
  } finally {
    loadingExams.value = false
  }
}

const showRoomDialog = () => {
  roomForm.value = { id: null, name: '', alias: '', building: '', capacity: 30 }
  roomDialogVisible.value = true
}

const editRoom = (row) => {
  roomForm.value = { ...row }
  roomDialogVisible.value = true
}

const submitRoom = async () => {
  if (!roomForm.value.name || !roomForm.value.building) {
    ElMessage.warning('请填写完整信息')
    return
  }
  submitting.value = true
  try {
    if (roomForm.value.id) {
      await examApi.updateRoom(roomForm.value.id, roomForm.value)
      ElMessage.success('考场更新成功')
    } else {
      await examApi.createRoom(roomForm.value)
      ElMessage.success('考场创建成功')
    }
    roomDialogVisible.value = false
    fetchRooms()
  } catch (e) {
    console.error(e)
  } finally {
    submitting.value = false
  }
}

const deleteRoom = async (id) => {
  try {
    await examApi.deleteRoom(id)
    ElMessage.success('考场已删除')
    fetchRooms()
  } catch (e) {
    console.error(e)
  }
}

const showExamDialog = () => {
  let defaultGroupsStr = ''
  
  if (exams.value && exams.value.length > 0) {
    const sorted = [...exams.value].sort((a, b) => b.id - a.id)
    const examWithGroups = sorted.find(e => e.grade_rule_groups)
    if (examWithGroups) {
      defaultGroupsStr = examWithGroups.grade_rule_groups
    }
  }
  
  examForm.value = {
    id: null,
    name: '',
    description: '',
    grade_rule_type: 'isolated',
    grade_rule_groups: defaultGroupsStr
  }
  
  try {
    if (defaultGroupsStr) {
      let parsed = JSON.parse(defaultGroupsStr)
      parsed = parsed.map(group => group.filter(g => allPossibleGrades.value.includes(g))).filter(group => group.length > 0)
      
      const assigned = new Set(parsed.flat())
      allPossibleGrades.value.forEach(g => {
        if (!assigned.has(g)) {
          parsed.push([g])
        }
      })
      customGroups.value = parsed
    } else {
      customGroups.value = allPossibleGrades.value.map(g => [g])
    }
  } catch (e) {
    customGroups.value = allPossibleGrades.value.map(g => [g])
  }
  
  examDialogVisible.value = true
}

const editExam = (row) => {
  examForm.value = {
    id: row.id,
    name: row.name,
    description: row.description,
    grade_rule_type: row.grade_rule_type || 'isolated',
    grade_rule_groups: row.grade_rule_groups || ''
  }
  try {
    if (row.grade_rule_groups) {
      let parsed = JSON.parse(row.grade_rule_groups)
      parsed = parsed.map(group => group.filter(g => allPossibleGrades.value.includes(g))).filter(group => group.length > 0)
      
      const assigned = new Set(parsed.flat())
      allPossibleGrades.value.forEach(g => {
        if (!assigned.has(g)) {
          parsed.push([g])
        }
      })
      customGroups.value = parsed
    } else {
      customGroups.value = allPossibleGrades.value.map(g => [g])
    }
  } catch (e) {
    customGroups.value = allPossibleGrades.value.map(g => [g])
  }
  examDialogVisible.value = true
}

const submitExam = async () => {
  if (!examForm.value.name) {
    ElMessage.warning('请输入考试名称')
    return
  }
  
  let groupsPayload = null
  if (['custom', 'cross'].includes(examForm.value.grade_rule_type)) {
    groupsPayload = getGroupsPayload()
    try {
      const parsed = JSON.parse(groupsPayload)
      if (!parsed || parsed.length === 0 || !parsed.some(g => g.length > 0)) {
        throw new Error()
      }
    } catch (e) {
      ElMessage.error('自定义分组配置不能为空')
      return
    }
  }
  
  submitting.value = true
  try {
    const payload = {
      name: examForm.value.name,
      description: examForm.value.description,
      grade_rule_type: examForm.value.grade_rule_type,
      grade_rule_groups: groupsPayload
    }
    if (examForm.value.id) {
      await examApi.update(examForm.value.id, payload)
      ElMessage.success('考试更新成功')
    } else {
      await examApi.create({
        ...payload,
        semester_id: selectedSemesterId.value
      })
      ElMessage.success('考试创建成功')
    }
    examDialogVisible.value = false
    fetchExams()
  } catch (e) {
    console.error(e)
  } finally {
    submitting.value = false
  }
}

const deleteExam = async (id) => {
  try {
    await examApi.delete(id)
    ElMessage.success('考试已删除')
    fetchExams()
  } catch (e) {
    console.error(e)
  }
}

const configureExam = async (exam) => {
  currentExam.value = exam
  configDialogVisible.value = true
}

const reloadCurrentExam = async () => {
  if (!currentExam.value?.id) return
  try {
    currentExam.value = await examApi.getById(currentExam.value.id)
  } catch (e) {
    console.error(e)
  }
}

const addSession = async () => {
  if (!sessionForm.value.exam_date || !sessionForm.value.start_time || !sessionForm.value.end_time) {
    ElMessage.warning('请填写日期和时间')
    return
  }
  try {
    await examApi.addSession(currentExam.value.id, sessionForm.value)
    ElMessage.success('场次添加成功')
    sessionForm.value = {
      exam_date: sessionForm.value.exam_date, // 保留日期方便连续添加
      start_time: '',
      end_time: '',
      session_name: '',
      subject_id: null,
      grade: sessionForm.value.grade // 保留年级
    }
    await reloadCurrentExam()
    fetchExams()
  } catch (e) {
    console.error(e)
  }
}

const deleteSession = async (id) => {
  try {
    await examApi.deleteSession(id)
    ElMessage.success('场次已删除')
    await reloadCurrentExam()
    fetchExams()
  } catch (e) {
    console.error(e)
  }
}

const showRoomAssignDialog = (session) => {
  currentSession.value = session
  selectedRoomIds.value = session.exam_rooms?.map(r => r.room_id) || []
  selectAllRooms.value = selectedRoomIds.value.length > 0 && selectedRoomIds.value.length === rooms.value.length
  applyToAllSessions.value = false
  applyToGradeThisExam.value = false
  applyToGradeOtherExams.value = false
  roomAssignForm.value = { required_invigilators: 1 }
  roomAssignDialogVisible.value = true
}

const submitRoomAssignment = async () => {
  submitting.value = true
  try {
    if (applyToAllSessions.value && currentExam.value?.sessions?.length) {
      const promises = currentExam.value.sessions.map(s => {
        const isCurrentSession = s.id === currentSession.value.id
        return examApi.assignRoomsToSession(
          s.id,
          selectedRoomIds.value,
          roomAssignForm.value.required_invigilators,
          isCurrentSession ? applyToGradeThisExam.value : false,
          isCurrentSession ? applyToGradeOtherExams.value : false
        )
      })
      await Promise.all(promises)
    } else {
      await examApi.assignRoomsToSession(
        currentSession.value.id,
        selectedRoomIds.value,
        roomAssignForm.value.required_invigilators,
        applyToGradeThisExam.value,
        applyToGradeOtherExams.value
      )
    }
    ElMessage.success('考场配置成功')
    roomAssignDialogVisible.value = false
    await reloadCurrentExam()
    fetchExams()
  } catch (e) {
    console.error(e)
  } finally {
    submitting.value = false
  }
}

const removeRoomFromSession = async (sessionId, roomId) => {
  try {
    await examApi.removeRoomFromSession(sessionId, roomId)
    ElMessage.success('考场已移除')
    await reloadCurrentExam()
    fetchExams()
  } catch (e) {
    console.error(e)
  }
}

const showBatchImportDialog = () => {
  batchImportText.value = ''
  parsedSessions.value = []
  batchImportMode.value = 'excel'
  sessionImportExcelFile.value = null
  sessionImportUploadRef.value?.clearFiles()
  batchImportDialogVisible.value = true
}

const downloadSessionTemplate = async () => {
  try {
    const blob = await examApi.downloadSessionImportTemplate()
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = '考试场次导入模板.xlsx'
    link.click()
    window.URL.revokeObjectURL(url)
  } catch (e) {
    console.error(e)
    ElMessage.error('下载模板失败')
  }
}

const handleSessionExcelChange = (uploadFile) => {
  sessionImportExcelFile.value = uploadFile.raw
}

const handleSessionExcelRemove = () => {
  sessionImportExcelFile.value = null
}

const parseBatchText = () => {
  if (!batchImportText.value.trim()) {
    parsedSessions.value = []
    return
  }

  const lines = batchImportText.value.split('\n')
  const results = []

  lines.forEach(line => {
    let text = line.trim()
    if (!text) return

    // 1. Extract Date
    const dateRegex = /(\d{4})[-/.－〜—–_~～\u2014\u2013\uFF5E]+(\d{1,2})[-/.－〜—–_~～\u2014\u2013\uFF5E]+(\d{1,2})/
    const dateMatch = text.match(dateRegex)
    if (!dateMatch) return
    
    const dateStr = `${dateMatch[1]}-${dateMatch[2].padStart(2, '0')}-${dateMatch[3].padStart(2, '0')}`
    text = text.replace(dateMatch[0], ' ')

    // 2. Extract Time Range
    const timeRegex = /(\d{1,2}[:：]\d{2}(?:[:：]\d{2})?)\s*[-~－〜—–_～\u2014\u2013\uFF5E]+\s*(\d{1,2}[:：]\d{2}(?:[:：]\d{2})?)/
    const timeMatch = text.match(timeRegex)
    if (!timeMatch) return

    const rawStart = timeMatch[1]
    const rawEnd = timeMatch[2]
    
    const normalizeTime = (t) => {
      let clean = t.replace(/：/g, ':').trim()
      const parts = clean.split(':')
      const hh = parts[0].padStart(2, '0')
      const mm = parts[1].padStart(2, '0')
      const ss = parts.length === 3 ? parts[2].padStart(2, '0') : '00'
      return `${hh}:${mm}:${ss}`
    }
    
    const startTime = normalizeTime(rawStart)
    const endTime = normalizeTime(rawEnd)
    text = text.replace(timeMatch[0], ' ')

    // 3. Extract Grade
    let grade = ''
    for (const g of allPossibleGrades.value) {
      if (text.includes(g)) {
        grade = g
        text = text.replace(g, ' ')
        break
      }
    }
    
    if (!grade) {
      const gradeRegex = /([初高][一二三123])|([一二三四五六七八九十123456789]+年级)/
      const gradeMatch = text.match(gradeRegex)
      if (gradeMatch) {
        grade = gradeMatch[0]
        text = text.replace(gradeMatch[0], ' ')
      }
    }

    // 4. Extract Subject
    let remaining = text.replace(/[,，;；|]/g, ' ').trim()
    remaining = remaining.replace(/\s+/g, ' ')
    
    let subjectName = remaining
    let matchedSubject = subjects.value.find(s => s.name === subjectName)
    
    if (!matchedSubject && subjectName) {
      matchedSubject = subjects.value.find(s => subjectName.includes(s.name) || s.name.includes(subjectName))
      if (matchedSubject) {
        subjectName = matchedSubject.name
      }
    }

    results.push({
      exam_date: dateStr,
      start_time: startTime,
      end_time: endTime,
      grade: grade || '未知',
      subject_id: matchedSubject?.id || null,
      subject_name: subjectName || '未知',
      rooms: []
    })
  })

  parsedSessions.value = results
}

const submitBatchImport = async () => {
  if (batchImportMode.value === 'excel' && !sessionImportExcelFile.value) {
    ElMessage.warning('请选择 Excel 文件')
    return
  }
  if (batchImportMode.value === 'text' && parsedSessions.value.length === 0) return

  submitting.value = true
  try {
    if (batchImportMode.value === 'excel') {
      const result = await examApi.importSessionsExcel(currentExam.value.id, sessionImportExcelFile.value)
      const errorHint = result.errors?.length ? `，${result.errors.length} 行未导入` : ''
      ElMessage.success((result.message || '导入成功') + errorHint)
      sessionImportExcelFile.value = null
      sessionImportUploadRef.value?.clearFiles()
    } else {
      await examApi.batchAddSessions(currentExam.value.id, {
        sessions: parsedSessions.value
      })
      ElMessage.success(`成功导入 ${parsedSessions.value.length} 个场次`)
    }
    batchImportDialogVisible.value = false
    await reloadCurrentExam()
    fetchExams()
  } catch (e) {
    console.error(e)
    ElMessage.error('导入失败，请检查格式是否正确')
  } finally {
    submitting.value = false
  }
}

const formatTime = (time) => {
  if (!time) return ''
  return time.substring(0, 5) // HH:mm:ss -> HH:mm
}

const getGradeTagType = (grade) => {
  if (!grade) return 'info'
  const types = ['success', 'warning', 'danger', 'primary', 'info']
  const idx = allPossibleGrades.value.indexOf(grade)
  if (idx !== -1) {
    return types[idx % types.length]
  }
  // fallback for unknown grades based on hash
  let hash = 0
  for (let i = 0; i < grade.length; i++) {
    hash = grade.charCodeAt(i) + ((hash << 5) - hash)
  }
  return types[Math.abs(hash) % types.length]
}

const goToSchedule = (examId) => {
  router.push({ name: 'Schedules', params: { examId } })
}


const showBatchImportRoomsDialog = () => {
  batchImportRoomsText.value = ''
  parsedRooms.value = []
  batchImportRoomsDialogVisible.value = true
}

const parseBatchRoomsText = () => {
  if (!batchImportRoomsText.value.trim()) {
    parsedRooms.value = []
    return
  }

  const lines = batchImportRoomsText.value.split('\n')
  const results = []

  lines.forEach(line => {
    if (!line.trim()) return

    // Split by whitespace
    const parts = line.trim().split(/\s+/)
    if (parts.length < 2) return

    const building = parts[0]
    const name = parts[1]
    
    // Default capacity to 30 if not specified or invalid
    let capacity = 30
    if (parts.length >= 3) {
      const parsedCap = parseInt(parts[2])
      if (!isNaN(parsedCap)) {
        capacity = parsedCap
      }
    }
    
    const alias = parts.length >= 4 ? parts.slice(3).join(' ') : null

    results.push({
      building,
      name,
      capacity,
      alias
    })
  })

  parsedRooms.value = results
}

const submitBatchRoomsImport = async () => {
  if (parsedRooms.value.length === 0) return

  submitting.value = true
  try {
    const res = await examApi.batchImportRooms({
      rooms: parsedRooms.value
    })
    ElMessage.success(res.message || '导入成功')
    batchImportRoomsDialogVisible.value = false
    fetchRooms()
  } catch (e) {
    console.error(e)
    ElMessage.error('导入失败，请检查数据格式')
  } finally {
    submitting.value = false
  }
}

onMounted(() => {
  fetchSemesters()
  fetchSubjects()
  fetchRooms()
})
</script>

<style scoped>
.exam-management {
  max-width: 1600px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header span {
  font-size: 16px;
  font-weight: 500;
}

.config-section {
  padding: 10px 0;
}

h4 {
  color: #303133;
  font-weight: 500;
}

.import-hint {
  background-color: #f0f9eb;
  border-left: 5px solid #67c23a;
  padding: 10px 15px;
  margin-bottom: 15px;
  font-size: 13px;
  color: #606266;
}

.import-hint p {
  margin: 4px 0;
}

.preview-section {
  margin-top: 20px;
}

.preview-section h4 {
  margin-bottom: 10px;
}
</style>
